import { NAMED_THEMES, tokenCatalog, type ThemeName, type TokenGroup } from '@church/ui-tokens'
import { useEffect, useState } from 'react'

/**
 * Live tweaks to the design tokens, for staging and development: a colour,
 * a row's padding, a corner, a duration, changed on the page as you watch.
 *
 * Tweaks are inline custom properties on `<html>`, which outrank the theme's
 * own (`.dark`, `[data-theme]`). Colours belong to the theme they were made
 * in, so Ember's tweaks leave Linen alone; everything else applies in every
 * theme. They live in this browser only (`forhim.design-tweaks`) until they
 * are copied out and made permanent in packages/ui-tokens.
 */
export const designToolsEnabled = import.meta.env.DEV || import.meta.env.VITE_DESIGN_TOOLS === '1'

const KEY = 'forhim.design-tweaks'

/** Opens the design editor (the profile drawer's Design editor row). */
export const DESIGN_EDITOR_EVENT = 'forhim:design-editor'
export function openDesignEditor() {
  window.dispatchEvent(new Event(DESIGN_EDITOR_EVENT))
}

export interface Tweaks {
  /** Per theme: CSS variable → value. */
  themed: Partial<Record<ThemeName, Record<string, string>>>
  /** Every theme: CSS variable → value. */
  global: Record<string, string>
}

const empty = (): Tweaks => ({ themed: {}, global: {} })

function load(): Tweaks {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return empty()
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && 'themed' in parsed && 'global' in parsed)
      return parsed as Tweaks
  } catch {
    // Unreadable or blocked: start clean.
  }
  return empty()
}

function save(tweaks: Tweaks) {
  try {
    localStorage.setItem(KEY, JSON.stringify(tweaks))
  } catch {
    // This visit only.
  }
}

/** The theme on the page now, as the tokens name it. */
export function currentTheme(): ThemeName {
  const root = document.documentElement
  const named = root.dataset.theme
  if (named && (NAMED_THEMES as readonly string[]).includes(named)) return named as ThemeName
  return root.classList.contains('dark') ? 'dark' : 'light'
}

/** Every token's variable: what a tweak may set, and so what clearing one removes. */
const tokenNames = new Set(tokenCatalog('light').flatMap((g) => g.tokens.map((t) => t.name)))

/** Every tweak is cleared, then the current theme's and the global ones are set. */
function apply(tweaks: Tweaks) {
  const style = document.documentElement.style
  for (const name of tokenNames) style.removeProperty(name)
  for (const [name, value] of Object.entries(tweaks.global)) style.setProperty(name, value)
  for (const [name, value] of Object.entries(tweaks.themed[currentTheme()] ?? {}))
    style.setProperty(name, value)
}

/** Applies saved tweaks before the first render and follows theme changes. Once, at boot. */
export function applySavedTweaks() {
  if (!designToolsEnabled) return
  apply(load())
  new MutationObserver(() => apply(load())).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'data-theme'],
  })
}

export function useDesignTweaks() {
  const [tweaks, setTweaks] = useState(load)
  const [theme, setTheme] = useState(currentTheme)

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(currentTheme()))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    })
    return () => observer.disconnect()
  }, [])

  const commit = (next: Tweaks) => {
    setTweaks(next)
    save(next)
    apply(next)
  }

  const groups: TokenGroup[] = tokenCatalog(theme)

  const valueOf = (group: TokenGroup, name: string): string | undefined =>
    group.themed ? tweaks.themed[theme]?.[name] : tweaks.global[name]

  const set = (group: TokenGroup, name: string, value: string | null) => {
    if (group.themed) {
      const own = { ...tweaks.themed[theme] }
      if (value === null) delete own[name]
      else own[name] = value
      commit({ ...tweaks, themed: { ...tweaks.themed, [theme]: own } })
    } else {
      const global = { ...tweaks.global }
      if (value === null) delete global[name]
      else global[name] = value
      commit({ ...tweaks, global })
    }
  }

  const count =
    Object.keys(tweaks.global).length +
    Object.values(tweaks.themed).reduce((n, own) => n + Object.keys(own ?? {}).length, 0)

  /** The changes as text a person can read and paste back to make them permanent. */
  const exportText = (): string => {
    const lines = [`ForHim design tweaks · ${new Date().toISOString().slice(0, 10)}`]
    const defaults = new Map<string, string>()
    for (const t of Object.keys(tweaks.themed) as ThemeName[])
      for (const group of tokenCatalog(t))
        for (const token of group.tokens) defaults.set(`${t}:${token.name}`, token.value)
    for (const group of tokenCatalog('light'))
      for (const token of group.tokens) defaults.set(`*:${token.name}`, token.value)
    for (const [t, own] of Object.entries(tweaks.themed)) {
      const changed = Object.entries(own ?? {})
      if (changed.length === 0) continue
      lines.push('', `Theme ${t}`)
      for (const [name, value] of changed)
        lines.push(`  ${name}: ${defaults.get(`${t}:${name}`) ?? '?'} → ${value}`)
    }
    const global = Object.entries(tweaks.global)
    if (global.length > 0) {
      lines.push('', 'Every theme')
      for (const [name, value] of global)
        lines.push(`  ${name}: ${defaults.get(`*:${name}`) ?? '?'} → ${value}`)
    }
    lines.push('', 'JSON', JSON.stringify(tweaks))
    return lines.join('\n')
  }

  const resetAll = () => commit(empty())
  const resetTheme = () => {
    const themed = { ...tweaks.themed }
    delete themed[theme]
    commit({ ...tweaks, themed })
  }
  /** Takes a pasted export (its JSON line) and applies it. */
  const importText = (text: string): boolean => {
    const json = text.trim().split('\n').at(-1) ?? ''
    try {
      const parsed: unknown = JSON.parse(json)
      if (parsed && typeof parsed === 'object' && 'themed' in parsed && 'global' in parsed) {
        commit(parsed as Tweaks)
        return true
      }
    } catch {
      // Not an export.
    }
    return false
  }

  return { theme, groups, valueOf, set, count, exportText, resetAll, resetTheme, importText }
}
